import React, { useEffect, useRef, useState } from "react";
import { Loader2, Camera, UserCircle2, Cake, Save, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthImage from "@/components/AuthImage";
import { STANDARDS } from "@/lib/standards";

export default function MyProfile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: "", student_class: "", date_of_birth: "", parent_name: "", parent_contact: "", parent_contact_alt: "", email: "" });
  const [photoKey, setPhotoKey] = useState(0);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((x) => ({ ...x, [k]: e.target.value }));

  const load = async () => {
    try {
      const { data } = await api.get("/student/profile");
      setData(data);
      const p = data.profile || {};
      setForm({ name: p.name || "", student_class: p.student_class || "", date_of_birth: p.date_of_birth || "", parent_name: p.parent_name || "", parent_contact: p.parent_contact || "", parent_contact_alt: p.parent_contact_alt || "", email: p.guardian_email || "" });
    } catch { toast.error("Could not load your profile"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Only send fields the student actually filled; blanks never erase existing values.
      const payload = {};
      ["name", "student_class", "date_of_birth", "parent_name", "parent_contact", "parent_contact_alt", "email"].forEach((k) => {
        if (form[k] && form[k].trim() !== "") payload[k] = form[k].trim();
      });
      const { data } = await api.post("/student/profile/request", payload);
      toast.success(data.message || "Submitted for review");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/student/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message || "Photo uploaded for review");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;
  const p = data?.profile || {};
  const pend = data?.pending_request;

  return (
    <div className="space-y-6" data-testid="my-profile">
      {data?.birthday && (
        <div className="rounded-3xl bg-gradient-to-br from-pink-500 to-amber-400 text-white p-6 shadow-xl flex items-center gap-4" data-testid="birthday-banner">
          <Cake className="h-10 w-10" />
          <div><p className="text-xl font-extrabold">Happy Birthday, {p.name}! 🎉</p>
            <p className="text-white/90 text-sm">Everyone at PTG wishes you a wonderful day.</p></div>
        </div>
      )}

      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8">
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-5"><UserCircle2 className="h-5 w-5" /> My Profile</h2>
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex flex-col items-center gap-3">
            <div className="h-32 w-32 rounded-2xl overflow-hidden border border-slate-200 bg-slate-50">
              {p.has_photo ? <AuthImage key={photoKey} path={`/student-photo/${user.id}`} alt="Profile" className="h-32 w-32 object-cover" />
                : <div className="h-32 w-32 flex items-center justify-center text-slate-300"><UserCircle2 className="h-16 w-16" /></div>}
            </div>
            {p.photo_pending && <Badge variant="warning" className="text-[10px]" data-testid="photo-pending-badge"><Clock className="h-3 w-3 mr-1" /> New photo awaiting approval</Badge>}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="photo-input" />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="photo-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Camera className="h-4 w-4 mr-1" /> Add / Change Photo</>}
            </Button>
            <p className="text-[11px] text-slate-400 text-center max-w-[160px]">Optional. New photos appear after PTG approval; your current photo stays until then.</p>
          </div>
          <div className="flex-1 grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-[11px] uppercase text-slate-400">Name</div><div className="font-semibold text-slate-800">{p.name || "—"}</div></div>
            <div><div className="text-[11px] uppercase text-slate-400">Roll Number</div><div className="font-semibold text-slate-800">{p.roll_number || "—"}</div></div>
            <div><div className="text-[11px] uppercase text-slate-400">Class</div><div className="font-semibold text-slate-800">{p.student_class || "—"}</div></div>
            <div><div className="text-[11px] uppercase text-slate-400">Date of birth</div><div className="font-semibold text-slate-800">{p.date_of_birth || "Not set"}</div></div>
            <div><div className="text-[11px] uppercase text-slate-400">Parent / guardian</div><div className="font-semibold text-slate-800">{p.parent_name || "—"}</div></div>
            <div><div className="text-[11px] uppercase text-slate-400">Parent contact</div><div className="font-semibold text-slate-800">{p.parent_contact || "—"}</div></div>
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8">
        <h3 className="text-lg font-bold text-slate-900 mb-1">Complete / Update Profile</h3>
        <p className="text-sm text-slate-500 mb-4">Submit changes to your contact details and date of birth. Your current details stay in place until PTG approves the changes.</p>
        {pend && (
          <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="profile-pending">
            <Clock className="h-4 w-4" /> You have changes awaiting PTG review: {Object.keys(pend.fields || {}).join(", ")}.
          </div>
        )}
        <form onSubmit={submit} className="space-y-3" data-testid="profile-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1 block font-semibold text-slate-700">Roll number (read-only)</Label>
              <Input value={p.roll_number || ""} readOnly className="bg-slate-100" data-testid="profile-roll" /></div>
            <div><Label className="mb-1 block font-semibold text-slate-700">Std / class</Label>
              <Select value={form.student_class || undefined} onValueChange={(v) => setForm((x) => ({ ...x, student_class: v }))}>
                <SelectTrigger data-testid="profile-class"><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent>{STANDARDS.map((s) => <SelectItem key={s} value={s} data-testid={`profile-class-${s}`}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label className="mb-1 block font-semibold text-slate-700">Full name</Label>
            <Input value={form.name} onChange={set("name")} data-testid="profile-name" /></div>
          <div><Label className="mb-1 block font-semibold text-slate-700">Date of birth</Label>
            <Input type="date" value={form.date_of_birth || ""} onChange={set("date_of_birth")} max={new Date().toISOString().slice(0, 10)} data-testid="profile-dob" />
            {!form.date_of_birth && <p className="text-xs text-slate-400 mt-1">Not set — pick your real birth date.</p>}</div>
          <div><Label className="mb-1 block font-semibold text-slate-700">Parent / guardian name</Label>
            <Input value={form.parent_name} onChange={set("parent_name")} data-testid="profile-parent-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1 block font-semibold text-slate-700">Parent contact</Label>
              <Input value={form.parent_contact} onChange={set("parent_contact")} placeholder="+91XXXXXXXXXX" data-testid="profile-contact" /></div>
            <div><Label className="mb-1 block font-semibold text-slate-700">Additional contact</Label>
              <Input value={form.parent_contact_alt} onChange={set("parent_contact_alt")} placeholder="optional" data-testid="profile-contact-alt" /></div>
          </div>
          <div><Label className="mb-1 block font-semibold text-slate-700">Email (optional)</Label>
            <Input type="email" value={form.email} onChange={set("email")} placeholder="optional backup email" data-testid="profile-email" />
            <p className="text-xs text-slate-500 mt-1">A changed email must be re-verified by PTG before it can be used for password reset.</p></div>
          <Button type="submit" disabled={busy} className="bg-blue-900 hover:bg-blue-950" data-testid="profile-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Submit changes for review</>}
          </Button>
        </form>
      </div>
    </div>
  );
}
